import React from 'react';
import { Select, MenuItem } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { useAtom } from 'jotai';
import { AppContext } from '../../../App';
import DialogGlynt, { FormControlStyled, FormLabelStyled } from '../../common/Dialog/DialogGlynt';
import DialogTitleGlynt from '../../common/Dialog/DialogTitleGlynt';
import DialogActionsGlynt from '../../common/Dialog/DialogActionsGlynt';
import DialogContentGlynt from '../../common/Dialog/DialogContentGlynt';
import { packageEditDialogPackageIdAtom } from '../../../atoms/packager-atoms';
import { PackageStatuses } from '../../../types/packager-types';
import { useGetPackages } from '../../hooks/packages/useGetPackages';
import { extractApiError } from '../../../utils/extractApiError';

import { usePackageUpdate } from '../../hooks/packages/usePackageUpdate';

interface IPackageEditStatusProps {}

const PackageEditStatus: React.FC<IPackageEditStatusProps> = ({}) => {
  const { setToast } = React.useContext(AppContext);
  const queryClient = useQueryClient();
  const [packageEditDialogPackageId, setPackageEditDialogPackageId] = useAtom(
    packageEditDialogPackageIdAtom
  );
  const [selectedStatus, setSelectedStatus] = React.useState<PackageStatuses | ''>('');

  const { data: packages } = useGetPackages();

  // Find the current package
  const currentPackage = packages?.results?.find((pkg) => pkg.id === packageEditDialogPackageId);

  // Set the selected status when the package changes
  React.useEffect(() => {
    if (currentPackage?.status) {
      setSelectedStatus(currentPackage.status);
    }
  }, [currentPackage]);

  const mutationPackageUpdate = usePackageUpdate({
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['packager']
      });
      queryClient.invalidateQueries({
        queryKey: ['packages']
      });
      // The detail query gates entry edits in PackagerDocuments.
      queryClient.invalidateQueries({ queryKey: ['package'] });
      setToast({
        variant: 'success',
        open: true,
        message: 'Package status updated!',
      });
      setPackageEditDialogPackageId(null);
    },
    onError: (error: AxiosError) => {
      setToast({
        variant: 'error',
        open: true,
        message: `Failed to update status: ${extractApiError(error)}`,
      });
    },
  });

  const closeModal = () => {
    setPackageEditDialogPackageId(null);
    setSelectedStatus('');
  };

  const handleConfirmSend = () => {
    mutationPackageUpdate.mutate({
      packageId: packageEditDialogPackageId!,
      data: { status: selectedStatus || undefined },
    });
  };

  const handleStatusChange = (event: any) => {
    setSelectedStatus(event.target.value);
  };

  return (
    <>
      <DialogGlynt
        open={packageEditDialogPackageId !== null}
        closeModal={closeModal}
        width="400px"
        cypressId="dialog-package-send-confirmation"
      >
        <DialogTitleGlynt title="Edit Package Status" closeModal={closeModal} />
        <DialogContentGlynt>
          <FormControlStyled fullWidth>
            <FormLabelStyled>PACKAGE STATUS</FormLabelStyled>
            <Select
              value={selectedStatus}
              onChange={handleStatusChange}
              variant="outlined"
              size="small"
              data-cy="package-status-select"
            >
              {/*
                Source the dropdown from per-instance available_transitions only — the lifecycle is
                authoritative. ORPHANED is filtered out: it's universally reachable but only ever
                set by signal when the parent Packager is deleted, never by an operator.
              */}
              {[
                ...(currentPackage?.status ? [currentPackage.status] : []),
                ...(currentPackage?.available_transitions ?? []).filter(
                  (s) => s !== PackageStatuses.ORPHANED
                ),
              ].map((status) => (
                <MenuItem
                  key={status}
                  value={status}
                  data-cy={`package-status-${status.toLowerCase()}`}
                >
                  {status}
                </MenuItem>
              ))}
            </Select>
          </FormControlStyled>
        </DialogContentGlynt>
        <DialogActionsGlynt
          handleSubmit={handleConfirmSend}
          closeModal={closeModal}
          cancelCypressId="btn-dialog-send-cancel"
          submitCypressId="btn-dialog-send-confirm"
          clearCypressId="btn-dialog-send-clear"
          submitButtonText="Update Status"
          submitLoading={mutationPackageUpdate.isPending}
          showCancelButton={true}
          showClearButton={false}
          submitDisabled={mutationPackageUpdate.isPending}
        />
      </DialogGlynt>
    </>
  );
};

export default PackageEditStatus;
